import { FC, useCallback, useMemo, useState } from 'react';
import { MRT_Row, MRT_RowData, MRT_SortingState } from 'material-react-table';

import { ShipmentChargeDto } from 'src/generated/services/TFinancialApi';
import { useUpsertShipmentCharge } from 'src/use-cases/serviceProvider/useServiceProvider';
import { formatNumber } from 'src/utils/utility';
import { ContractManagerColumnHeader } from '../ContractManagerColumnHeader';
import { RateCardChargesTableBase } from './TableBase';
import { getTableColumns } from './utils';
import { ITableProps } from './types';
import { createFilterOptions, applyFiltersAndSorting, commonBooleanAccessors } from './filterUtils';

export const RateCardChargesTableShipment: FC<ITableProps> = (props) => {
  const { contractFlow, chargeCategory, tableRef, filters, setFilters, sorting, setSorting, isAdmin } = props;
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});

  // Original unfiltered data
  const originalData = useMemo(() => contractFlow?.[chargeCategory] ?? [], [contractFlow, chargeCategory]);

  // Define accessors for boolean fields
  const accessors = useMemo(() => commonBooleanAccessors, []);

  // Apply filters and sorting with accessors
  const tableData = useMemo(() => applyFiltersAndSorting(originalData, filters, accessors, sorting), [originalData, filters, accessors, sorting]);
  // Handle sorting and filtering
  const handleTableSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      setSorting([{ id: columnId, desc: direction === 'desc' }]);
    },
    [setSorting]
  );

  // Handle filter changes
  const handleFilter = useCallback(
    (columnId: string, selectedValues: string[]) => {
      setFilters((prev) => ({
        ...prev,
        [columnId]: selectedValues,
      }));
    },
    [setFilters]
  );

  const columns = useMemo(() => {
    const shipmentColumns = [
      {
        accessorKey: 'unitPrice',
        header: 'UNIT PRICE',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='unitPrice'
            originalData={originalData}
            currentFilters={filters?.unitPrice || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
        Cell: ({ row }) => (
          <>
            {row?.original.unitPrice || parseFloat(row?.original.unitPrice.toString()) === 0
              ? `${formatNumber(parseFloat(row?.original.unitPrice?.toString()))}`
              : '-'}
          </>
        ),
      },
      {
        accessorKey: 'currency',
        header: 'CURRENCY',
        enableSorting: false,
        Header: ({ column }) => (
          <ContractManagerColumnHeader
            column={column}
            accessorKey='currency'
            originalData={originalData}
            currentFilters={filters?.currency || []}
            onSort={handleTableSort}
            onFilter={handleFilter}
          />
        ),
      },
    ];

    const allColumns = getTableColumns(shipmentColumns, validationErrors, setValidationErrors, {
      filters,
      handleFilter,
      handleSort: handleTableSort,
      originalData,
      createFilterOptions,
    });

    return allColumns;
  }, [validationErrors, originalData, handleFilter, handleTableSort, filters]);

  const { mutateAsync: upsertShipmentCharge } = useUpsertShipmentCharge();

  const onValidateCharge = useCallback((charge: Record<string, any>) => {
    const validationResult = {
      chargeName: !charge.chargeName || charge.chargeName === '' ? 'Required' : '',
      chargeCategory: !charge.chargeCategory || charge.chargeCategory === '' ? 'Required' : '',
      legType: !charge.legType || charge.legType === '' ? 'Required' : '',
      currency: !charge.currency || charge.currency === '' ? 'Required' : '',
      unitPrice: (!charge.unitPrice || charge.unitPrice === '') && charge.unitPrice !== 0 ? 'Required' : '',
    };

    return validationResult;
  }, []);

  const onUpsertCharge = useCallback(
    async (row: MRT_Row<MRT_RowData>, values: Record<string, any>) => {
      const reqBody: ShipmentChargeDto & { contractFlowId: string; deviationExcluded?: boolean } = {
        id: row.original?.id || null,
        contractFlowId: contractFlow?.id,
        legType: values.legType,
        chargeName: values.chargeName,
        chargeCategory: values.chargeCategory,
        accessorial: values.accessorial.toLowerCase() === 'true',
        approval: values.approval.toLowerCase() === 'true',
        outlay: values.outlay.toLowerCase() === 'true',
        deviationExcluded: values.deviationExcluded ? values.deviationExcluded.toLowerCase() === 'true' : false,
        unitPrice: values.unitPrice,
        currency: values.currency || null,
      };

      await upsertShipmentCharge(reqBody);
    },
    [upsertShipmentCharge, contractFlow]
  );

  return (
    <RateCardChargesTableBase
      tableRef={tableRef}
      tableData={tableData}
      columns={columns}
      sorting={sorting}
      contractFlow={contractFlow}
      chargeCategory={chargeCategory}
      onUpsertCharge={onUpsertCharge}
      setValidationErrors={setValidationErrors}
      onValidateCharge={onValidateCharge}
      isAdmin={isAdmin}
    />
  );
};
